import tokens from '@kluein/design-tokens';
import {
  Box,
  Button,
  Select,
  type SelectItemProps,
  Text,
  TextInput,
} from '@kluein/klue-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  filterRivalsByCategory,
  normalizeSelectOptions,
} from 'pages/dashboard/utils/askKlue.utils';
import store from 'store';

import type {
  AskKlueQuestionWizardProps,
  AskKlueWizardInputType,
} from '../../AskKlueModalWizard.types';

function InputQuestion(props: AskKlueQuestionWizardProps) {
  const { onAnswer, id, isDisabled } = props;
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>('');

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: newValue } = event.target;
    setInputValue(newValue);
  };

  const onTextInputKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = event;
      if (key === 'Enter' && !isDisabled) {
        onAnswer(inputValue, id);
      }
    },
    [onAnswer, inputValue, id, isDisabled],
  );

  return (
    <Box gap="small" direction="row" align="center" margin={{ top: 'xlarge' }}>
      <TextInput
        ref={inputRef}
        aria-label={t('Dashboard:askKlue.answerModal.wizard.input.placeholder')}
        onChange={onChange}
        placeholder={t(
          'Dashboard:askKlue.answerModal.wizard.input.placeholder',
        )}
        plain="full"
        type="text"
        value={inputValue}
        variant="default"
        onKeyDown={onTextInputKeyPress}
        disabled={isDisabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <Box flex={{ grow: 1, shrink: 0 }}>
        <Button
          variant="secondary"
          size="small"
          label={t('Common:actions.submit')}
          onClick={() => onAnswer(inputValue, id)}
          disabled={isDisabled}
        />
      </Box>
    </Box>
  );
}

const selectAllRivals = store.select(({ rivals: { all } }) => ({
  allRivals: all,
}));

function RivalQuestion(props: AskKlueQuestionWizardProps) {
  const { onAnswer, id, isDisabled } = props;
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>();
  const { allRivals } = useSelector(selectAllRivals);

  const rivalsList = allRivals.sort((rivalA, rivalB) =>
    rivalA.name.localeCompare(rivalB.name),
  );

  const rivalsData = useMemo(
    () => normalizeSelectOptions(allRivals, search),
    [allRivals, search],
  );

  const firstThreeRivals = filterRivalsByCategory(rivalsList).slice(0, 3);

  return (
    <Box gap="small" direction="row" align="center" margin={{ top: 'xlarge' }}>
      {firstThreeRivals.map((item) => (
        <Button
          key={item.id}
          variant="secondary"
          size="small"
          label={item.name}
          disabled={isDisabled}
          onClick={() => onAnswer(item.name, id)}
        />
      ))}
      <Box flex={{ grow: 1, shrink: 0 }}>
        <Select
          options={rivalsData}
          size="small"
          onSelected={(item) => {
            onAnswer((item as SelectItemProps).label, id);
          }}
          onSearch={setSearch}
          onClose={() => setSearch(undefined)}
          searchPlaceholder={t('Common:search.label')}
          a11yTitle={t(
            'Dashboard:askKlue.answerModal.wizard.select.placeholder',
          )}
          placeholder={t(
            'Dashboard:askKlue.answerModal.wizard.select.placeholder',
          )}
          disabled={isDisabled}
        />
      </Box>
    </Box>
  );
}

function AskKlueQuestionWizard(props: AskKlueQuestionWizardProps) {
  const { question } = props;

  const componentMapRender = (type: AskKlueWizardInputType) => {
    const componentMap = {
      input: <InputQuestion {...props} />,
      rival: <RivalQuestion {...props} />,
    };
    return componentMap[type];
  };

  return (
    <Box pad={{ left: 'xlarge', vertical: 'small' }}>
      <Box flex={{ grow: 1, shrink: 0 }}>
        <Text
          lineHeight={tokens.lineHeight.dense}
          fontFamily={tokens.fontFamily.Inter}
          size={tokens.fontSize.bodyFont.medium}
          color={tokens.color.neutral.darkgrey.main}
          style={{
            whiteSpace: 'pre-line',
          }}
        >
          {question}
        </Text>
        <Box>{componentMapRender(props.inputType)}</Box>
      </Box>
    </Box>
  );
}

export default AskKlueQuestionWizard;
